// Where the driver is and where each delivery was made: the phone's position
// while the delivery list (or the driver view) is open, sent to the run now
// and then and saved with each stop recorded, and how far that was from the
// delivery address.

import { useCallback, useEffect, useRef, useState } from "react";

import { placesOf } from "./routeMapStops";
import { routeLegs, straightMeters } from "./runDistance";

// Further than this from the address, a stop's spot shows in red: worth a
// look (the wrong door, or the GPS was off).
export const FAR_METERS = 150;
// The driver's position goes to the run about this often, or after moving
// this far.
export const PING_MS = 60000;
export const PING_METERS = 100;
// A position this recent still counts for a stop recorded now; otherwise
// recording waits this long for the next one.
export const FRESH_MS = 120000;
export const WAIT_MS = 6000;
// The position shown on screen moves after this far or this long.
const SHOW_METERS = 10;
const SHOW_MS = 15000;

const KEY = "routes:shareLocation";

// "32 m", "1.2 km", "14 km".
export const metersText = (meters) => {
  if (meters == null || !Number.isFinite(Number(meters))) return "";
  const m = Number(meters);
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;
};

// "just now", "4 min ago", "2 h ago", "3 d ago".
export const agoText = (date, now = Date.now()) => {
  if (date == null || date === "") return "";
  const time = new Date(date).getTime();
  if (!Number.isFinite(time)) return "";
  const seconds = Math.max(0, Math.round((now - time) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
};

/**
 * Whether this device shares its location on a run: as chosen here before,
 * else yes on a phone or tablet (where the driver is) and no on a computer
 * (the office), so the office's own position never passes for the driver's.
 */
export const readSharing = () => {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "on") return true;
    if (saved === "off") return false;
  } catch {
    // Not remembered: the default.
  }
  return Boolean(window.matchMedia?.("(pointer: coarse)")?.matches);
};

export const saveSharing = (on) => {
  try {
    localStorage.setItem(KEY, on ? "on" : "off");
  } catch {
    // The choice just isn't remembered.
  }
};

const fixOf = (position) => ({
  lat: position.coords.latitude,
  lng: position.coords.longitude,
  accuracy: position.coords.accuracy,
  at: position.timestamp || Date.now(),
});

/**
 * The phone's position while `enabled`: { state, fix, getFix }.
 * - state: "off", "waiting", "on", "denied" (blocked in the browser) or
 *   "unavailable" (the browser can't).
 * - fix: the latest { lat, lng, accuracy, at }, updated on screen after
 *   SHOW_METERS or SHOW_MS.
 * - getFix(): a position from the last FRESH_MS, or the next one within
 *   WAIT_MS, else null (straight away when blocked); never longer, so
 *   recording a stop isn't held up.
 * `onPing(fix)` hears the position about every PING_MS or PING_METERS (the
 * first one straight away).
 */
export const useDriverLocation = ({ enabled, onPing }) => {
  const supported =
    typeof navigator !== "undefined" && Boolean(navigator.geolocation);
  const [state, setState] = useState("off");
  const [fix, setFix] = useState(null);
  const latest = useRef(null);
  const shown = useRef(null);
  const pinged = useRef(null);
  const waiters = useRef([]);
  // Blocked: recording a stop doesn't wait for a position that won't come.
  const blocked = useRef(false);
  const pingRef = useRef(onPing);
  pingRef.current = onPing;

  useEffect(() => {
    if (!enabled) {
      setState("off");
      return undefined;
    }
    if (!supported) {
      setState("unavailable");
      return undefined;
    }
    setState("waiting");
    const id = navigator.geolocation.watchPosition(
      (position) => {
        const next = fixOf(position);
        latest.current = next;
        blocked.current = false;
        setState("on");
        waiters.current.splice(0).forEach((resolve) => resolve(next));
        const was = shown.current;
        if (
          !was ||
          straightMeters(was, next) >= SHOW_METERS ||
          next.at - was.at >= SHOW_MS
        ) {
          shown.current = next;
          setFix(next);
        }
        const last = pinged.current;
        if (
          !last ||
          straightMeters(last, next) >= PING_METERS ||
          next.at - last.at >= PING_MS
        ) {
          pinged.current = next;
          pingRef.current?.(next);
        }
      },
      (error) => {
        if (error?.code !== 1) return;
        blocked.current = true;
        setState("denied");
        waiters.current.splice(0).forEach((resolve) => resolve(null));
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 },
    );
    return () => {
      navigator.geolocation.clearWatch(id);
      waiters.current.splice(0).forEach((resolve) => resolve(null));
    };
  }, [enabled, supported]);

  const getFix = useCallback(() => {
    const now = latest.current;
    if (now && Date.now() - now.at <= FRESH_MS) return Promise.resolve(now);
    if (!enabled || !supported || blocked.current) return Promise.resolve(null);
    return new Promise((resolve) => {
      let timer = null;
      const done = (value) => {
        clearTimeout(timer);
        resolve(value);
      };
      timer = setTimeout(() => {
        waiters.current = waiters.current.filter((wait) => wait !== done);
        resolve(null);
      }, WAIT_MS);
      waiters.current.push(done);
    });
  }, [enabled, supported]);

  return { state, fix, getFix };
};

/**
 * Where Google put each address of the run's drive (`points` as
 * loopPoints makes them), from the drive the sheet already asked for, so no
 * new request: a Map of address → { lat, lng }; empty when there's none.
 */
export const drivePlaces = async (points) => {
  if (!Array.isArray(points) || points.length < 2) return new Map();
  const legs = await routeLegs(points).catch(() => null);
  return legs ? placesOf(points, legs) : new Map();
};

/**
 * What a recorded stop sends about where it happened: { lat, lng,
 * accuracy, distance } (distance in metres from `place`, the address on the
 * map, when known), or null without a position.
 */
export const stopLocation = (fix, place) => {
  if (!fix) return null;
  const location = {
    lat: Number(fix.lat.toFixed(7)),
    lng: Number(fix.lng.toFixed(7)),
    accuracy: Math.round(fix.accuracy ?? 0),
  };
  if (place) location.distance = Math.round(straightMeters(fix, place));
  return location;
};

// A run stop's saved spot, or null: { place, distance, accuracy, far }.
export const stopSpot = (stop) => {
  const lat = Number(stop?.done_lat);
  const lng = Number(stop?.done_lng);
  if (stop?.done_lat == null || !Number.isFinite(lat) || !Number.isFinite(lng))
    return null;
  const distance =
    stop.done_distance == null ? null : Number(stop.done_distance);
  return {
    place: { lat, lng },
    distance,
    accuracy: stop.done_accuracy == null ? null : Number(stop.done_accuracy),
    far: distance != null && distance > FAR_METERS,
  };
};

// The run's driver, or null: { place, accuracy, seenAt }.
export const driverSpot = (run) => {
  const lat = Number(run?.driver_lat);
  const lng = Number(run?.driver_lng);
  if (run?.driver_lat == null || !Number.isFinite(lat) || !Number.isFinite(lng))
    return null;
  return {
    place: { lat, lng },
    accuracy: run.driver_accuracy == null ? null : Number(run.driver_accuracy),
    seenAt: run.driver_seen_at ?? null,
  };
};

/**
 * The stop nearest to `place` among `stops` ([{ number, customer, address }])
 * placed on the map by `places` (address → { lat, lng }): { stop, meters },
 * or null.
 */
export const nearestStop = (place, stops, places) => {
  let best = null;
  stops.forEach((stop) => {
    const at = places.get(stop.address);
    if (!at) return;
    const meters = straightMeters(place, at);
    if (!best || meters < best.meters) best = { stop, meters };
  });
  return best;
};
